import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Map, Link, Server, AlertTriangle, Shield, CheckSquare,
  FileText, GraduationCap, Settings, Trash2, History, MessageSquare, Briefcase,
  Activity, ClipboardCheck, AlertCircle, Archive, Bug, ShieldCheck
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api.js'

const navItems = [
  {
    section: 'Overview',
    links: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true },
      { to: '/my-work', label: 'My Work', icon: ClipboardCheck },
    ]
  },
  {
    section: 'Organization',
    links: [
      { to: '/scope', label: 'Scope Definition', icon: FileText },
      { to: '/processes', label: 'Process Register', icon: Briefcase },
    ]
  },
  {
    section: 'GRC Core',
    links: [
      { to: '/assets', label: 'Asset Register', icon: Server },
      { to: '/vulnerabilities', label: 'Vulnerabilities', icon: Bug },
      { to: '/risks', label: 'Risk Register', icon: AlertTriangle },
      { to: '/controls-library', label: 'Controls Library', icon: Archive },
      { to: '/controls', label: 'Annex A Controls', icon: CheckSquare, exact: true },
    ]
  },
  {
    section: 'Compliance & Assurance',
    links: [
      { to: '/soa', label: 'Statement of Applicability', icon: FileText },
      { to: '/clauses', label: 'Clause Compliance', icon: Activity },
      { to: '/review-center', label: 'Review Center', icon: ShieldCheck, showBadge: true },
      { to: '/audits', label: 'Internal Audits', icon: ClipboardCheck },
      { to: '/non-conformity', label: 'Non-Conformity & CAPA', icon: AlertCircle },
    ]
  },
  {
    section: 'Resources',
    links: [
       { to: '/documents', label: 'Document Library', icon: Archive },
       { to: '/recycle-bin', label: 'Recycle Bin', icon: Trash2 },
    ]
  },
  {
    section: 'Security & Admin',
    links: [
       { to: '/audit-logs', label: 'Audit Logs', icon: History },
    ]
  },
]

export function Sidebar({ user }) {
  const location = useLocation()

  const { data: pending = [] } = useQuery({ 
    queryKey: ['reviews', 'pending'], 
    queryFn: api.getPendingReviews,
    refetchInterval: 30000 // Refresh every 30s to keep badge accurate
  })

  // Filter navigation items based on user role
  const filteredNavItems = navItems.map(section => {
    const filteredLinks = section.links.filter(link => {
      const role = user?.role || 'Contributor';

      // Admin can see everything
      if (role === 'Admin') return true;

      // Manager role restrictions
      if (role === 'Manager') {
        if (link.to === '/audit-logs' || link.to === '/settings') return false;
        return true;
      }

      // Auditor role restrictions
      if (role === 'Auditor') {
        // Auditor cannot see settings, audit-logs, recycle-bin, or review-center
        if (link.to === '/recycle-bin' || link.to === '/review-center') return false;
        return true;
      }

      if (role === 'Contributor' || role === 'User') {
        const allowedEmployeeRoutes = ['/my-work', '/processes', '/documents', '/risks', '/vulnerabilities', '/controls-library'];
        return allowedEmployeeRoutes.includes(link.to);
      }

      return false;
    });

    if (filteredLinks.length === 0) return null;

    return {
      ...section,
      links: filteredLinks
    };
  }).filter(Boolean);

  return (
    <nav className="sidebar">
      <div className="sidebar-logo">
        <div className="sidebar-logo-icon">
          <img src="https://wissda.com/assets/brand/wissda-logo-on-dark.svg" alt="Wissda Logo" className="logo-img" />
        </div>
        <div className="sidebar-logo-text">
          <div className="title">ISO 27001</div>
          <div className="subtitle">Compliance Platform</div>
        </div>
      </div>

      <div className="sidebar-nav">
        {filteredNavItems.map(section => (
          <div key={section.section}>
            <div className="nav-section-label">{section.section}</div>
            {section.links.map(link => {
              const Icon = link.icon
              const isActive = link.exact
                ? location.pathname === link.to
                : location.pathname.startsWith(link.to)
              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={`nav-item ${isActive ? 'active' : ''}`}
                >
                  <Icon size={16} />
                  <span style={{ flex: 1 }}>{link.label}</span>
                  {link.showBadge && pending.length > 0 && (
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                      (<span style={{ color: 'var(--danger)' }}>{pending.length}</span>)
                    </span>
                  )}
                </NavLink>
              )
            })}
          </div>
        ))}
      </div>

      <div className="sidebar-footer">
        {user?.role === 'Admin' && (
          <NavLink to="/settings" className="nav-item">
            <Settings size={16} />
            Settings
          </NavLink>
        )}
      </div>
    </nav>
  )
}
